import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Snackbar,
  Alert,
  AlertColor,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { useNavigate } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import RequiredLabel from "../../atoms/RequiredLabel";
import {
  emailErrorMessage,
  emailRegex,
  phoneNumberErrorMessage,
  phoneNumberRegex,
} from "../../../utils/ValidationHelper";
import ConfirmDialog from "../../molecules/ConfirmDialog";

const CHARACTER_LIMIT = 50; // 文字数制限を定義
const CHARACTER_LIMIT_SUBSCRIBE_NUMBER = 10;

const OwnerRegistrationPage: React.FC = () => {
  const navigate = useNavigate();

  const [id, setId] = useState("(登録時に自動生成されます)");
  const [password, setPassword] = useState("(登録時に自動生成されます)");
  const [subscribeNumber, setSubscribeNumber] = useState("");

  const [ownerName, setOwnerName] = useState("");
  const [departmentName, setDepartmentName] = useState("");

  const [representativeName, setRepresentativeName] = useState("");
  const [inChargeName, setInChargeName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [memo, setMemo] = useState("");
  const [isEnabled, setIsEnabled] = useState("有効");

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [openConfirm, setOpenConfirm] = React.useState(false);

  const [isTouched, setIsTouched] = useState({
    subscribeNumber: false,
    ownerName: false,
    sdcServiceName: false,
    departmentName: false,
    representativeName: false,
    inChargeName: false,
    phoneNumber: false,
    email: false,
    memo: false,
    isEnabled: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    subscribeNumber: "",
    ownerName: "",
    departmentName: "",
    representativeName: "",
    inChargeName: "",
    phoneNumber: "",
    email: "",
    memo: "",
    isEnabled: "",
  });

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      subscribeNumber:
        isTouched.subscribeNumber && !subscribeNumber
          ? "契約者番号は必須です"
          : isTouched.subscribeNumber &&
            subscribeNumber.length > CHARACTER_LIMIT_SUBSCRIBE_NUMBER
          ? `契約者番号は${CHARACTER_LIMIT_SUBSCRIBE_NUMBER}桁以内で入力してください。`
          : "",
      ownerName:
        isTouched.ownerName &&
        ownerName !== "" &&
        ownerName.length > CHARACTER_LIMIT
          ? `名称・氏名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      departmentName:
        isTouched.departmentName &&
        departmentName !== "" &&
        departmentName.length > CHARACTER_LIMIT
          ? `会社名(部署名)は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      representativeName:
        isTouched.representativeName &&
        representativeName !== "" &&
        representativeName.length > CHARACTER_LIMIT
          ? `代表者名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      inChargeName:
        isTouched.inChargeName &&
        inChargeName !== "" &&
        inChargeName.length > CHARACTER_LIMIT
          ? `担当者名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      phoneNumber:
        isTouched.phoneNumber &&
        phoneNumber &&
        !phoneNumber.match(phoneNumberRegex)
          ? phoneNumberErrorMessage
          : "",
      email:
        isTouched.email && email && !emailRegex.test(email)
          ? emailErrorMessage
          : "",
      memo:
        isTouched.memo && memo !== "" && memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      isEnabled:
        isTouched.isEnabled && !isEnabled ? "状態を選択してください" : "",
    };

    setValidationErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTouched,
    subscribeNumber,
    ownerName,
    departmentName,
    representativeName,
    inChargeName,
    phoneNumber,
    email,
    memo,
  ]);

  const handleFormSubmit = async (event: React.FormEvent) => {
    setIsSubmitting(true);
    event.preventDefault();

    if (!subscribeNumber || !isEnabled) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        subscribeNumber: !subscribeNumber
          ? "契約者番号は必須です"
          : prevErrors.subscribeNumber,
        isEnabled: !isEnabled ? "状態を選択してください" : prevErrors.isEnabled,
      }));
      setIsSubmitting(false);
      return; // エラーがあるため、保存操作を中断
    }

    // エラーがある場合、保存を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      setIsSubmitting(false);
      return;
    }

    const owner = {
      subscribeNumber,
      ownerName,
      departmentName,
      representativeName,
      picName: inChargeName,
      ownerPhone: phoneNumber,
      ownerEmail: email,
      memo,
      isEnabled: isEnabled === "有効",
    };

    console.log(owner);

    const jwtToken = await getJwtTokenFromIdToken();

    try {
      // call storeOwner API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT + "/owners",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify(owner),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setId(data.owner.ownerId);
        setPassword(data.owner.password);
        console.log("LAD契約者（オーナー）新規登録が完了しました。");
        setOpenConfirm(true);
      } else if (response.status === 409) {
        const errorData = await response.json();
        console.error("契約者番号が重複しています:", errorData);
        setSnackbarMessage("契約者番号が重複しています。");
        setAlertSeverity("error");
        setSnackbarOpen(true);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("LAD契約者（オーナー）新規登録に失敗しました。", error);
      setSnackbarMessage("LAD契約者（オーナー）新規登録に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    }
    setIsSubmitting(false);
  };

  const handleCancelClick = () => {
    console.log(`戻るがクリックされました`);
    navigate(-1);
  };

  return (
    <CustomContainer maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle title={"LAD契約者（オーナー）新規登録"} />
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleFormSubmit}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>
                      <Typography>{id}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>パスワード</TableCell>
                    <TableCell>
                      <Typography>{password}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      契約者番号
                      <RequiredLabel />
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="契約者番号"
                        value={subscribeNumber}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setSubscribeNumber(e.target.value)}
                        onBlur={() => handleBlur("subscribeNumber")}
                        helperText={validationErrors.subscribeNumber}
                        error={validationErrors.subscribeNumber !== ""}
                        inputProps={{
                          maxLength: CHARACTER_LIMIT_SUBSCRIBE_NUMBER,
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      名称(氏名)
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="名称・氏名"
                        value={ownerName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setOwnerName(e.target.value)}
                        onBlur={() => handleBlur("ownerName")}
                        helperText={validationErrors.ownerName}
                        error={validationErrors.ownerName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      会社名(部署名)
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="会社名(部署名)"
                        value={departmentName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setDepartmentName(e.target.value)}
                        onBlur={() => handleBlur("departmentName")}
                        helperText={validationErrors.departmentName}
                        error={validationErrors.departmentName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>代表者名</TableCell>
                    <TableCell>
                      <TextField
                        label="代表者名"
                        value={representativeName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setRepresentativeName(e.target.value)}
                        onBlur={() => handleBlur("representativeName")}
                        helperText={validationErrors.representativeName}
                        error={validationErrors.representativeName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>担当者名</TableCell>
                    <TableCell>
                      <TextField
                        label="担当者名"
                        value={inChargeName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setInChargeName(e.target.value)}
                        onBlur={() => handleBlur("inChargeName")}
                        helperText={validationErrors.inChargeName}
                        error={validationErrors.inChargeName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      連絡先(TEL)
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="連絡先(TEL)"
                        value={phoneNumber}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        onBlur={() => handleBlur("phoneNumber")}
                        helperText={validationErrors.phoneNumber}
                        error={validationErrors.phoneNumber !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      連絡先(mail)
                    </TableCell>
                    <TableCell>
                      <TextField
                        label="連絡先(mail)"
                        value={email}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => handleBlur("email")}
                        helperText={validationErrors.email}
                        error={validationErrors.email !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>メモ</TableCell>
                    <TableCell>
                      <TextField
                        label="メモ"
                        multiline
                        rows={3}
                        value={memo}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setMemo(e.target.value)}
                        onBlur={() => handleBlur("memo")}
                        helperText={validationErrors.memo}
                        error={validationErrors.memo !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      状態
                    </TableCell>
                    <TableCell>
                      <RadioGroup
                        row
                        value={isEnabled}
                        onChange={(e) => setIsEnabled(e.target.value)}
                      >
                        <FormControlLabel
                          value="有効"
                          control={<Radio />}
                          label="有効"
                        />
                        <FormControlLabel
                          value="無効"
                          control={<Radio />}
                          label="無効"
                        />
                      </RadioGroup>
                      {validationErrors.isEnabled && (
                        <Typography color="error" variant="body2">
                          {validationErrors.isEnabled}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Box display={"flex"} justifyContent={"flex-end"} marginTop={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelClick}
              >
                戻る
              </Button>
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isSubmitting}
                sx={{ marginLeft: 1 }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    登録中...
                  </Box>
                ) : (
                  '登録'
                )}
              </Button>
            </Box>
          </form>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/owners`);
        }}
        title="確認"
        description="登録が完了しました。"
        showCancelButton={false}
      />
    </CustomContainer>
  );
};

export default OwnerRegistrationPage;
